import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { BroadcastService } from '../../common/services/broadcast/broadcast.service';
import { UserHttpService } from '../../user/services/user-http.service';
import { FavoriteEvents } from '../enums/favorite-events.enum';
import { GenericFavoriteService, FavoriteControlsDict, FavoriteControl, FavoriteControls } from '../models/favorite';
import { ToastService } from '../../common/services/toast/toast.service';
import { v4 } from 'uuid';
import { cloneDeep } from 'lodash';

@Injectable({
    providedIn: 'root'
})
export class FavoriteControlsService implements GenericFavoriteService<FavoriteControlsDict, FavoriteControl> {
    private key = 'favoriteControls';

    public favorites$: Observable<FavoriteControlsDict>;
    private favoritesSubject: BehaviorSubject<FavoriteControlsDict>;
    private favorites: FavoriteControlsDict;
    private serverFavorites: any[];

    constructor(
        private broadcastService: BroadcastService,
        private httpService: UserHttpService,
        private toastService: ToastService
    ) {
        this.initDefaultValues();
        this.getFavoritesData();
    }

    private initDefaultValues() {
        this.favorites = {};
        this.favoritesSubject = new BehaviorSubject<FavoriteControlsDict>(null);
        this.favorites$ = this.favoritesSubject.asObservable();
        this.serverFavorites = [];
    }

    private getFavoritesData() {
        this.getUserFavoritesControls().toPromise().then(result => {
            if (result != null) {
                for (const dictkey in result) {
                    if (result.hasOwnProperty(dictkey)) {
                        let favorite = result[dictkey];
                        //convert null to -1
                        favorite.spaceId = favorite.spaceId == null ? -1 : favorite.spaceId;

                        //store this cos it has server key
                        this.serverFavorites.push(favorite);

                        //we just need to build that favorites dictionary with arrays of control ids from our flat dictionary returned from service
                        this.addFavoriteImp(favorite);
                    }
                }
                this.notifyObserversFavoritesChanged();
            }
        }).catch(reason => {
            // ignore
        });
    }

    private notifyObserversFavoritesChanged() {
        const next = JSON.parse(JSON.stringify(this.favorites));
        this.favoritesSubject.next(next);
    }

    public toggleFavorite(favorite: FavoriteControl): void {
        if (this.isFavorite(favorite)) {
            this.removeFavorite(favorite);
        } else {
            this.addFavorite(favorite);
        }
    }

    public isFavorite(favorite: FavoriteControl) {
        const key = this.getDictKey(favorite);
        if (this.favorites[key]) {
            return this.favorites[key].controlIds.includes(favorite.controlId);
        }
        return false;
    }

    private getDictKey(favorite: FavoriteControl) {
        return favorite.locationId + ' ' + favorite.spaceId;
    }

    private getServerKeys(favorite: FavoriteControl): any[] {
        return this.serverFavorites.filter(item => item.locationId === favorite.locationId && item.spaceId === favorite.spaceId && item.controlId === favorite.controlId).map(item => item.id);
    }

    private removeFavorite(favorite: FavoriteControl) {
        const serverKeys = this.getServerKeys(favorite);
        for (let i = 0; i < serverKeys.length; i++) {
            this.deleteUserFavoritesControl(serverKeys[i]).toPromise().then(result => {
                if (result == true) {
                    const key = this.getDictKey(favorite);
                    let controlIds = this.favorites[key].controlIds;

                    controlIds = controlIds.filter(controlId => controlId !== favorite.controlId);
                    this.favorites[key].controlIds = controlIds;

                    if (controlIds.length === 0) {
                        delete this.favorites[key];
                    }

                    this.notifyObserversFavoritesChanged();
                    this.broadcastService.broadcast(FavoriteEvents.UnfavoriteControl, favorite);
                    this.toastService.showSimple('Removed from favorites.');
                }
                const ind: number = this.serverFavorites.findIndex(item => item.id == serverKeys[i]);
                if (ind != -1) {
                    this.serverFavorites.splice(ind, 1);
                }
            });
        }
    }

    private addFavorite(favorite: FavoriteControl) {
        this.postUserFavoritesControl(cloneDeep(favorite)).toPromise().then(result => {
            if (result != null) {
                //convert null to -1
                result.spaceId = result.spaceId == null ? -1 : result.spaceId;

                this.serverFavorites.push(result);

                this.addFavoriteImp(result);
                this.notifyObserversFavoritesChanged();
                this.broadcastService.broadcast(FavoriteEvents.FavoriteControl, result);
                this.toastService.showSimple('Added to favorites.');
            }
        });
    }

    private addFavoriteImp(favorite: FavoriteControl) {
        const key = this.getDictKey(favorite);
        if (this.favorites[key]) {
            if (this.favorites[key].controlIds.findIndex(item => item === favorite.controlId) == -1) {
                this.favorites[key].controlIds.push(favorite.controlId);
            }
        } else {
            this.favorites[key] = {
                locationId: favorite.locationId,
                spaceId: favorite.spaceId,
                controlIds: [favorite.controlId]
            } as FavoriteControls;
        }
    }

    private postUserFavoritesControl(favorite: any): Observable<any> {
        const id: string = v4();
        favorite['id'] = id;
        delete favorite['locationId'];
        delete favorite['spaceId'];
        return this.httpService.post<any>('user/favorites/controls', favorite);
    }

    private deleteUserFavoritesControl(id: string): Observable<boolean> {
        return this.httpService.delete<boolean>(`user/favorites/controls/${id}`);
    }

    private getUserFavoritesControls(): Observable<any> {
        return this.httpService.get<any>('user/favorites/controls');
    }
}
